import React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TablePagination from "@mui/material/TablePagination";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import type { RegulationRow } from "../../services/regulationsService";
import { BulkActions } from "./Toolbar/BulkActions";
import { RegulationInsightCard } from "./RegulationInsightCard";
import noDataAnim from "../../assets/no-data-anim.webp";
import Typography from "@mui/material/Typography";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

export type RegulationsCardViewProps = Readonly<{
  rows: RegulationRow[];
  loading: boolean;
  totalRows: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  selectedIds: readonly string[];
  onToggleSelected: (regulationId: string, selected: boolean) => void;
  /** `max_regulations_per_batch` from tenant config. Shown as "2/3 Selected". */
  maxSelectionCount: number;
  onFavoriteChange: (regulationId: string, isFavorite: boolean) => void;
  onBulkFavoritesSuccess?: (
    regulationIds: string[],
    isFavorite?: boolean,
  ) => void;
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  companyScopeStarting?: boolean;
}>;

export function RegulationsCardView({
  rows,
  loading,
  totalRows,
  paginationModel,
  onPaginationModelChange,
  selectedIds,
  onToggleSelected,
  maxSelectionCount,
  onFavoriteChange,
  onBulkFavoritesSuccess,
  onCompanyScopeImpact,
  onCustomScopeImpact,
  companyScopeStarting,
}: RegulationsCardViewProps) {
  const selected = new Set(selectedIds);
  const lookupRow = (id: string) => rows.find((row) => row.id === id);
  const pageSize =
    paginationModel.pageSize > 0 ? paginationModel.pageSize : 10;
  const lastPage =
    totalRows <= 0 ? 0 : Math.max(0, Math.ceil(totalRows / pageSize) - 1);
  const page = Math.min(Math.max(0, paginationModel.page), lastPage);
  const selectedCount = selectedIds.length;

  return (
    <Box
      data-testid="regulations-card-view"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: "12px 16px",
          bgcolor: "background.paper",
          boxShadow: "none",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#262626", fontSize: "16px" }}
          >
            Regulations List
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#8c8c8c", fontSize: "14px" }}
          >
            {selectedCount > 0
              ? `${selectedCount}/${maxSelectionCount} Selected`
              : ""}
          </Typography>
        </Box>
        <BulkActions
          lookupRow={lookupRow}
          onCompanyScopeImpact={onCompanyScopeImpact}
          onCustomScopeImpact={onCustomScopeImpact}
          companyScopeStarting={companyScopeStarting}
          onBulkFavoritesSuccess={onBulkFavoritesSuccess}
        />
      </Box>

      <Box
        data-testid="regulations-card-list"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          px: 1.5,
          pt: 2,
          pb: 2,
        }}
      >
        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} aria-label="Loading regulations" />
          </Box>
        ) : null}

        {!loading && rows.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 280,
              py: 4,
            }}
          >
            <Box
              component="img"
              src={noDataAnim}
              alt=""
              sx={{ width: 160, height: "auto", mb: 2 }}
            />
            <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
              No search results found.
            </Typography>
          </Box>
        ) : null}

        {rows.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 2,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {rows.map((row) => (
              <RegulationInsightCard
                key={row.id}
                row={row}
                selected={selected.has(row.id)}
                onToggleSelected={onToggleSelected}
                onFavoriteChange={onFavoriteChange}
              />
            ))}
          </Box>
        ) : null}
      </Box>

      <Box
        data-testid="regulations-card-pagination"
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 52,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: selectedCount > 0 ? "flex" : "none",
            alignItems: "center",
            mx: 2,
            fontSize: 14,
            color: "#262626",
            whiteSpace: "nowrap",
          }}
        >
          {selectedCount === 1
            ? "1 row selected"
            : `${selectedCount.toLocaleString()} rows selected`}
        </Box>
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(_event, nextPage) =>
            onPaginationModelChange({ ...paginationModel, page: nextPage })
          }
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) =>
            onPaginationModelChange({
              page: 0,
              pageSize: Number(event.target.value),
            })
          }
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          sx={{
            flexGrow: 1,
            border: "none",
            "& .MuiTablePagination-toolbar": {
              minHeight: 52,
              pl: selectedCount > 0 ? 0 : 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: 14,
                color: "#262626",
              },
          }}
        />
      </Box>
    </Box>
  );
}
